import React, { useState, useCallback, useEffect, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, Paper } from "@mui/material";
import Sidebar from "./sidebar/Sidebar";
import ConfirmStopModal from "../ConfirmStopModal";
import getPageType from "../../utils/pageType";
import { FONT_SIZE } from "../../../constants/fontSizes";
import { useIsDesktop } from "../hooks/useIsDesktop";

const Layout = ({
  currentUser,
  currentCompanyUser,
  userJobRequestExists: initialExists,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isDesktop = useIsDesktop();

  // 希望条件が存在するか
  const [userJobRequestExists, setUserJobRequestExists] =
    useState(!!initialExists);

  useEffect(() => {
    const handler = (e) => {
      setUserJobRequestExists(!!e.detail?.exists);
    };
    window.addEventListener("app:setUserJobRequestExists", handler);
    return () =>
      window.removeEventListener("app:setUserJobRequestExists", handler);
  }, []);

  // 未保存ガード用
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState(null);

  // 確認モーダルOK後に実行する処理を一時保持
  const [pendingAction, setPendingAction] = useState(null);

  const [isDirty, setIsDirty] = useState(false);

  // 未保存変更（dirty）状態を全画面共通で監視
  useEffect(() => {
    const handler = (e) => {
      setIsDirty(!!e.detail?.dirty);
    };
    window.addEventListener("app:setDirty", handler);
    return () => window.removeEventListener("app:setDirty", handler);
  }, []);

  // URLから画面種別を決定
  const pageType = useMemo(
    () => getPageType(location.pathname, location.search, currentUser),
    [location.pathname, location.search, currentUser],
  );

  // SP用：Drawer を閉じる
  const handleCloseMenu = () => {
    setMobileOpen(false);
  };

  // Header(= Rails側のReactヘッダー) から Sidebar を開閉する
  useEffect(() => {
    const toggleHandler = () => setMobileOpen((prev) => !prev);
    const closeHandler = () => setMobileOpen(false);

    window.addEventListener("app:toggleSidebar", toggleHandler);
    window.addEventListener("app:closeSidebar", closeHandler);

    return () => {
      window.removeEventListener("app:toggleSidebar", toggleHandler);
      window.removeEventListener("app:closeSidebar", closeHandler);
    };
  }, []);

  // CompanyUserでログイン中かどうか
  const isCompany = !!currentCompanyUser;

  // UserAdminかどうか
  const isUserAdmin = pageType == "admin";

  /**
   * Sidebar を表示する条件
   * - User：表示する
   * - UserAdmin：表示しない
   * - CompanyUser：role が "employee" のときだけ表示する
   */
  const showSidebar =
    !isUserAdmin && (!isCompany || currentCompanyUser?.role === "employee");

  // Deviseログアウト（destroy_user_session_path）
  const onClickLogout = useCallback(async () => {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    const logoutPath = isCompany
      ? "/company_users/sign_out"
      : "/users/sign_out";
    const redirectPath = isCompany
      ? "/company_users/sign_in"
      : "/users/sign_in";

    try {
      await fetch(logoutPath, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/html",
        },
      });

      window.location.href = redirectPath;
    } catch (e) {
      window.location.reload();
    }
  }, [isCompany]);

  // サイドバーの遷移要求
  const requestNavigate = useCallback(
    (to, action) => {
      // 未保存なら確認
      if (isDirty) {
        setPendingPath(to ?? null);
        setPendingAction(() => (typeof action === "function" ? action : null));
        setConfirmOpen(true);
        return;
      }

      // 未保存じゃないなら即実行
      handleCloseMenu();

      if (typeof action === "function") {
        action();
      } else if (to) {
        navigate(to);
      }
    },
    [isDirty, navigate],
  );

  // モーダル操作
  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false);
    setPendingPath(null);
    setPendingAction(null);
  }, []);

  // 未保存確認モーダルで「移動する」を選択した場合の最終遷移処理
  const handleConfirmMove = useCallback(() => {
    // dirty をクリア
    setIsDirty(false);
    window.dispatchEvent(
      new CustomEvent("app:setDirty", { detail: { dirty: false } }),
    );

    setConfirmOpen(false);
    handleCloseMenu();

    // action があればそれを実行、なければ path 遷移
    if (pendingAction) {
      pendingAction(); // ログアウト
    } else if (pendingPath) {
      navigate(pendingPath); // 通常遷移
    }

    setPendingPath(null);
    setPendingAction(null);
  }, [navigate, pendingPath, pendingAction]);

  // TOPページではサイドバーは表示しない
  const hasRouter = !!document.querySelector(".react-app-router");
  if (!hasRouter) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#F5F6FA" }}>
        <Outlet />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F5F6FA" }}>
      {/* サイドバー（PCは常時 / SPはDrawer） */}
      {showSidebar && (
        <Sidebar
          mobileOpen={mobileOpen}
          onClose={handleCloseMenu}
          onLogout={onClickLogout}
          isCompany={isCompany}
          currentUser={currentUser}
          currentCompanyUser={currentCompanyUser}
          requestNavigate={requestNavigate}
          userJobRequestExists={userJobRequestExists}
        />
      )}

      {/* 右側 */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          component="main"
          sx={{
            p: isDesktop ? 3 : 2,
            ml: isDesktop ? (showSidebar ? "240px" : 0) : 0,
            backgroundColor: "#F5F6FA",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* UserAdmin 表示中のカード */}
      {isUserAdmin && (
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            top: 60,
            left: 16,
            zIndex: (theme) => theme.zIndex.modal + 1,
            px: 1,
            py: 0.5,
            borderRadius: 2,
            backgroundColor: "#FFFBEA",
            color: "#856404",
            userSelect: "none",
            pointerEvents: "none",
          }}
        >
          <Box sx={{ fontSize: FONT_SIZE.XS, fontWeight: 600 }}>
            他ユーザー表示中
          </Box>
        </Paper>
      )}

      {/* 確認モーダル */}
      <ConfirmStopModal
        open={confirmOpen}
        onClose={handleCloseConfirm}
        onConfirm={handleConfirmMove}
        title="変更が保存されていません"
        message="未保存の変更があります。このまま移動すると入力内容が失われます。"
        subMessage="移動してもよろしいでしょうか？"
        confirmLabel="破棄して移動"
      />
    </Box>
  );
};

export default Layout;
